import { keepPreviousData, useQuery } from "@tanstack/react-query";
import dayjs from "dayjs";
import type { components } from "@qorakul/shared/api";
import { api } from "./api.ts";
import { ApiError } from "./apiError.ts";

export type Tariff = components["schemas"]["TariffDetails"];
export type ServiceKind = components["schemas"]["ServiceKindDetails"];
export type ServicePrice = components["schemas"]["ServicePriceDetails"];
export type TeacherRate = components["schemas"]["TeacherRateDetails"];
export type LivingOption = components["schemas"]["LivingOption"];
export type SubjectRateGroup = components["schemas"]["SubjectRateGroup"];
export type SubjectKind = components["schemas"]["SubjectKind"];

/** Fields every rule version shares; used by the validity and status cells. */
export interface RuleVersion {
  id: string;
  validFrom: string;
  validTo: null | string;
  usedThrough: null | string;
}

export const livingOptions: readonly LivingOption[] = ["Day", "Boarding"];
export const subjectRateGroups: readonly SubjectRateGroup[] = ["Core", "English", "Other"];
export const subjectKinds: readonly SubjectKind[] = ["Main", "Secondary"];
export const PAGE_SIZE = 50;

export const billingKeys = {
  all: ["billing"] as const,
  tariffs: (onDate: string | undefined, page: number) => ["billing", "tariffs", { onDate, page }] as const,
  servicePrices: (onDate: string | undefined, page: number) => ["billing", "service-prices", { onDate, page }] as const,
  serviceKinds: ["billing", "service-kinds"] as const,
  teacherRates: (onDate: string | undefined, page: number) => ["billing", "teacher-rates", { onDate, page }] as const,
  history: (kind: string, id: string) => ["billing", "history", kind, id] as const,
};

/** Today's school date as the API expects it ("YYYY-MM-DD"). */
export const todayIso = () => dayjs().format("YYYY-MM-DD");

/** "01.09.2026" for an API date. */
export const formatDate = (value: string) => dayjs(value).format("DD.MM.YYYY");

export function useTariffs(onDate: string | undefined, page: number) {
  return useQuery({
    queryKey: billingKeys.tariffs(onDate, page),
    placeholderData: keepPreviousData,
    queryFn: async () => {
      const { data, error, response } = await api.GET("/billing/tariffs", {
        params: { query: { ...(onDate ? { OnDate: onDate } : {}), Page: page, PageSize: PAGE_SIZE } },
      });
      if (!data) throw new ApiError(response.status, error);
      return data;
    },
  });
}

export function useServicePrices(onDate: string | undefined, page: number) {
  return useQuery({
    queryKey: billingKeys.servicePrices(onDate, page),
    placeholderData: keepPreviousData,
    queryFn: async () => {
      const { data, error, response } = await api.GET("/billing/service-prices", {
        params: { query: { ...(onDate ? { OnDate: onDate } : {}), Page: page, PageSize: PAGE_SIZE } },
      });
      if (!data) throw new ApiError(response.status, error);
      return data;
    },
  });
}

export function useServiceKinds() {
  return useQuery({
    queryKey: billingKeys.serviceKinds,
    queryFn: async () => {
      const { data, error, response } = await api.GET("/billing/service-kinds");
      if (!data) throw new ApiError(response.status, error);
      return data;
    },
  });
}

export function useTeacherRates(onDate: string | undefined, page: number) {
  return useQuery({
    queryKey: billingKeys.teacherRates(onDate, page),
    placeholderData: keepPreviousData,
    queryFn: async () => {
      const { data, error, response } = await api.GET("/billing/teacher-rates", {
        params: { query: { ...(onDate ? { OnDate: onDate } : {}), Page: page, PageSize: PAGE_SIZE } },
      });
      if (!data) throw new ApiError(response.status, error);
      return data;
    },
  });
}
